import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  MARKETING_DATE_FIELD,
  MARKETING_DATE_INPUT_FORMAT,
  MARKETING_DATE_OUTPUT_FORMAT,
  MARKETING_NUMERIC_FIELDS,
  MARKETING_PARAM_ALIASES,
  MARKETING_QUERY_PARAMS,
} from './config';
import type { IMarketingParams, IMarketingSlice } from './types';

/* `MMDDYYYY` is not an ISO-ish shape, so dayjs needs the strict-format plugin to read it. */
dayjs.extend(customParseFormat);

type ParsedQueryString = Omit<IMarketingSlice, 'params'> & {
  params: IMarketingParams;
};

function toNumber(value: string) {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Reads the landing URL's query string into the marketing slice's shape.
 *
 * Mirrors identityWeb's `new MarketingParams(params)` plus the sibling dispatches in
 * `app.component.ts:122-173`: an absent param is left out entirely rather than stored as an
 * empty string, `hid`/`campid` are coerced to numbers, and `camp_date` is reformatted.
 */
export default function parseMarketingQueryParams(
  search: string,
): ParsedQueryString {
  const searchParams = new URLSearchParams(search);

  const params = Object.entries(MARKETING_PARAM_ALIASES).reduce(
    (accumulator: IMarketingParams, [queryParam, field]) => {
      const value = searchParams.get(queryParam)?.trim();

      if (!value) {
        return accumulator;
      }

      if (field === MARKETING_DATE_FIELD) {
        const date = dayjs(value, MARKETING_DATE_INPUT_FORMAT, true);
        if (!date.isValid()) {
          return accumulator;
        }
        const formatted = date.format(MARKETING_DATE_OUTPUT_FORMAT);
        return { ...accumulator, [field]: formatted };
      }

      if (MARKETING_NUMERIC_FIELDS.includes(field)) {
        const numeric = toNumber(value);
        if (numeric === undefined) {
          return accumulator;
        }
        return { ...accumulator, [field]: numeric };
      }

      return { ...accumulator, [field]: value };
    },
    {},
  );

  /* identityWeb gates the employee id on a `group_code` being present alongside it. */
  const groupCode = searchParams.get(MARKETING_QUERY_PARAMS.groupCode)?.trim();
  const employeeId = searchParams
    .get(MARKETING_QUERY_PARAMS.employeeId)
    ?.trim();

  const freeSpiritNumber = searchParams
    .get(MARKETING_QUERY_PARAMS.freeSpiritNumber)
    ?.trim();
  const parsedFreeSpiritNumber = freeSpiritNumber
    ? toNumber(freeSpiritNumber)
    : undefined;

  return {
    params,
    employeeId: groupCode && employeeId ? employeeId : null,
    freeSpiritNumber: parsedFreeSpiritNumber ?? null,
    coBrand: searchParams.get(MARKETING_QUERY_PARAMS.coBrand)?.trim() || null,
    isEmbeddedInMobileApp:
      searchParams
        .get(MARKETING_QUERY_PARAMS.isEmbeddedInMobileApp)
        ?.trim()
        .toLowerCase() === 'true',
  };
}
